import type { FC, ReactNode } from 'react';
import { Button } from 'antd';
import { TbAlertTriangle } from 'react-icons/tb';

import { CompactHoverCardSurface } from '@/shared/components/cards';
import EmptyView from '@/shared/components/layouts/views/EmptySection';

import LifecycleIcon from '../LifecycleIcon';
import LifecyclePreviewPanel, { LifecyclePreviewFooter } from '../LifecyclePreviewPanel';
import LifecyclePreviewSkeleton, { LifecyclePreviewFooterSkeleton } from '../LifecyclePreviewSkeleton';
import type { LifecyclePreview } from '../types';

export type LifecyclePreviewState = 'empty' | 'error' | 'loading' | 'ready';

interface LifecyclePreviewSurfaceProps {
    emptyDescription?: ReactNode;
    errorDescription?: ReactNode;
    lifecycle?: LifecyclePreview;
    onRetry?: () => void;
    previewState?: LifecyclePreviewState;
    style?: React.CSSProperties;
}

/**
 * Shared Lifecycle preview body used by both Lifecycle enriched pills and the
 * workspace-resource preview registry. Keeping the shell here prevents the two
 * entry points from drifting as the preview evolves.
 */
const LifecyclePreviewSurface: FC<LifecyclePreviewSurfaceProps> = ({
    emptyDescription = 'Lifecycle details will appear here when they are available.',
    errorDescription = 'We could not load this lifecycle preview. Try again to restore the latest details.',
    lifecycle,
    onRetry,
    previewState,
    style,
}) => {
    const resolvedState: LifecyclePreviewState =
        previewState === 'ready' && !lifecycle ? 'empty' : (previewState ?? (lifecycle ? 'ready' : 'empty'));
    const content = (() => {
        if (resolvedState === 'loading') {
            return <LifecyclePreviewSkeleton />;
        }

        if (resolvedState === 'error') {
            return (
                <EmptyView
                    description={errorDescription}
                    extra={onRetry ? <Button onClick={onRetry}>Try Again</Button> : null}
                    height='20rem'
                    icon={<TbAlertTriangle size='1.25rem' />}
                    label='Unable to Load Lifecycle'
                />
            );
        }

        if (resolvedState === 'empty' || !lifecycle) {
            return (
                <EmptyView
                    description={emptyDescription}
                    height='20rem'
                    icon={<LifecycleIcon size='1.25rem' />}
                    label='No Lifecycle Preview'
                />
            );
        }

        return <LifecyclePreviewPanel lifecycle={lifecycle} showFooter={false} />;
    })();

    return (
        <CompactHoverCardSurface style={{ overflow: 'hidden', ...style }} width='30rem'>
            <div
                data-lifecycle-preview-scroll='true'
                style={{
                    // viewport-layout-allow: this surface is a viewport-bounded enriched-pill hover card.
                    maxHeight: 'min(37.75rem, calc(80vh - 2.25rem))',
                    overflowY: 'auto',
                    overscrollBehavior: 'contain',
                }}
            >
                <div aria-live='polite'>{content}</div>
            </div>
            {resolvedState === 'loading' ? <LifecyclePreviewFooterSkeleton /> : null}
            {resolvedState === 'ready' && lifecycle ? <LifecyclePreviewFooter lifecycle={lifecycle} /> : null}
        </CompactHoverCardSurface>
    );
};

export default LifecyclePreviewSurface;
